import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListChecks } from 'lucide-react';
import { sitesApi } from '../../api/client';
import { gettingStartedApi } from '../../api/modules/gettingStarted';
import type { Site } from '../../schemas/validation';
import {
  deriveGettingStartedState,
  dismissGettingStarted,
  GETTING_STARTED_CHANGE_EVENT,
  progressToFlags,
  type GettingStartedFlags,
} from './gettingStarted';
import { GettingStartedPanel } from './GettingStartedPanel';

interface GettingStartedHostProps {
  sites?: Site[];
  onCreateWebsite?: () => void;
}

const EMPTY_FLAGS: GettingStartedFlags = {
  openedSite: false,
  editedPage: false,
  dismissed: false,
};

const CELEBRATION_MS = 3500;

export const GettingStartedHost: React.FC<GettingStartedHostProps> = ({
  sites: sitesProp,
  onCreateWebsite,
}) => {
  const navigate = useNavigate();
  const [fetchedSites, setFetchedSites] = useState<Site[] | null>(null);
  const [flags, setFlags] = useState<GettingStartedFlags | null>(null);
  const [resolvedPageId, setResolvedPageId] = useState<string | null | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [hasInitializedOpen, setHasInitializedOpen] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  /** Bumps when flags change / panel opens / window focuses so progress re-derives. */
  const [progressVersion, setProgressVersion] = useState(0);
  const prevPublishedRef = useRef<boolean | null>(null);

  const sites = sitesProp ?? fetchedSites ?? [];
  const sitesReady = sitesProp !== undefined || fetchedSites !== null;
  const flagsReady = flags !== null;
  const ready = sitesReady && flagsReady;

  const refreshFetchedSites = useCallback(() => {
    if (sitesProp !== undefined) return;
    sitesApi
      .list()
      .then(list => setFetchedSites(list))
      .catch(() => setFetchedSites([]));
  }, [sitesProp]);

  const refreshProgress = useCallback(() => {
    gettingStartedApi
      .get()
      .then(progress => setFlags(progressToFlags(progress)))
      .catch(() => setFlags(EMPTY_FLAGS));
  }, []);

  useEffect(() => {
    if (sitesProp !== undefined) return;
    refreshFetchedSites();
  }, [sitesProp, refreshFetchedSites]);

  useEffect(() => {
    refreshProgress();
  }, [refreshProgress]);

  const bumpProgress = useCallback(() => {
    setProgressVersion(v => v + 1);
  }, []);

  useEffect(() => {
    const onChange = () => {
      bumpProgress();
      refreshFetchedSites();
      refreshProgress();
    };
    window.addEventListener(GETTING_STARTED_CHANGE_EVENT, onChange);
    window.addEventListener('focus', onChange);
    return () => {
      window.removeEventListener(GETTING_STARTED_CHANGE_EVENT, onChange);
      window.removeEventListener('focus', onChange);
    };
  }, [bumpProgress, refreshFetchedSites, refreshProgress]);

  const firstSite = sites[0];
  useEffect(() => {
    if (!firstSite?.id) {
      setResolvedPageId(null);
      return;
    }
    if (firstSite.homePageId) {
      setResolvedPageId(firstSite.homePageId);
      return;
    }

    let cancelled = false;
    sitesApi
      .getPages(firstSite.id)
      .then(pages => {
        if (!cancelled) setResolvedPageId(pages[0]?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) setResolvedPageId(null);
      });

    return () => {
      cancelled = true;
    };
  }, [firstSite?.id, firstSite?.homePageId, progressVersion]);

  const state = useMemo(
    () => deriveGettingStartedState(sites, flags ?? EMPTY_FLAGS, resolvedPageId ?? null),
    // progressVersion forces re-derive after external updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sites, flags, resolvedPageId, progressVersion]
  );

  useEffect(() => {
    if (!ready || hasInitializedOpen) return;
    setHasInitializedOpen(true);
    if (state.shouldAutoOpen) {
      setIsOpen(true);
    }
  }, [ready, hasInitializedOpen, state.shouldAutoOpen]);

  useEffect(() => {
    if (!ready) return;

    const prev = prevPublishedRef.current;
    const current = state.hasPublishedSite;
    if (prev === false && current === true) {
      setCelebrating(true);
      setIsOpen(true);
    }
    prevPublishedRef.current = current;
  }, [ready, state.hasPublishedSite]);

  useEffect(() => {
    if (!celebrating) return;
    const timeoutId = window.setTimeout(() => {
      setCelebrating(false);
      setIsOpen(false);
    }, CELEBRATION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [celebrating]);

  if (!ready || (!state.shouldShow && !celebrating)) {
    return null;
  }

  const handleClose = () => {
    if (celebrating) {
      setCelebrating(false);
      setIsOpen(false);
      return;
    }
    setIsOpen(false);
    setFlags(prev => ({ ...(prev ?? EMPTY_FLAGS), dismissed: true }));
    dismissGettingStarted();
  };

  const handleOpen = () => {
    bumpProgress();
    refreshFetchedSites();
    refreshProgress();
    setIsOpen(true);
  };

  const handleCreateWebsite = () => {
    if (onCreateWebsite) {
      onCreateWebsite();
      return;
    }
    navigate('/dashboard?create=1');
  };

  return (
    <>
      {!isOpen && (
        <button
          type="button"
          onClick={handleOpen}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-primary-600 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center group"
          title="Get started"
          aria-label="Open get started checklist"
          data-testid="getting-started-fab"
        >
          <ListChecks className="w-6 h-6 group-hover:scale-110 transition-transform" />
          {state.completedCount < state.steps.length && (
            <span className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 rounded-full bg-white text-primary-700 text-xs font-semibold flex items-center justify-center shadow">
              {state.steps.length - state.completedCount}
            </span>
          )}
        </button>
      )}

      <GettingStartedPanel
        isOpen={isOpen}
        onClose={handleClose}
        state={state}
        onCreateWebsite={handleCreateWebsite}
        celebration={celebrating}
      />
    </>
  );
};

export default GettingStartedHost;
